import { type Component, type JSX, For, Show, createEffect, createSignal, onCleanup } from 'solid-js';
import { ChevronDown, Clock } from 'lucide-solid';
import { cn } from '~/shared/lib/cn';
import { Popover } from './Popover';

interface TimePickerProps {
  value: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
  step?: number;
  size?: 'sm' | 'md' | 'lg';
  icon?: Component<{ class?: string }> | null;
  disabled?: boolean;
  placeholder?: string;
  align?: 'start' | 'center' | 'end';
  class?: string;
  ariaLabel?: string;
}



const SIZE_CLASS = {
  sm: 'h-8 px-2.5 text-[12.5px]',
  md: 'h-10 px-3 text-[14px]',
  lg: 'h-11 px-3 text-[14px]',
} as const;

const toMin = (hhmm: string): number => {
  const [h, m] = hhmm.split(':').map(Number);
  return (Number.isFinite(h) ? h! : 0) * 60 + (Number.isFinite(m) ? m! : 0);
};
const toHHMM = (mins: number): string =>
  `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;

export function TimePicker(props: TimePickerProps): JSX.Element {
  const [open, setOpen] = createSignal(false);
  const [anchor, setAnchor] = createSignal<HTMLElement>();
  let listRef: HTMLDivElement | undefined;

  const slots = (): string[] => {
    const step = props.step && props.step > 0 ? props.step : 30;
    const lo = toMin(props.min ?? '00:00');
    const hi = toMin(props.max ?? (step % 15 === 0 ? '23:45' : '23:30'));
    const out: string[] = [];
    for (let t = lo; t <= hi; t += step) out.push(toHHMM(t));
    return out;
  };

  const toggle = () => {
    if (props.disabled) return;
    setOpen((o) => !o);
  };

  createEffect(() => {
    if (!open()) return;
    const raf = requestAnimationFrame(() => {
      listRef?.querySelector('[data-selected="true"]')?.scrollIntoView({
        block: 'center',
        behavior: 'instant' as ScrollBehavior,
      });
    });
    onCleanup(() => cancelAnimationFrame(raf));
  });

  const Icon = props.icon === null ? null : (props.icon ?? Clock);

  return (
    <>
      <button
        type="button"
        ref={setAnchor}
        disabled={props.disabled}
        aria-haspopup="listbox"
        aria-expanded={open()}
        aria-label={props.ariaLabel ?? 'Выбрать время'}
        onClick={toggle}
        class={cn(
          'press w-full rounded-md ring-hairline inline-flex items-center gap-2 text-left tabular transition-colors disabled:opacity-50',
          SIZE_CLASS[props.size ?? 'md'],
          props.class,
        )}
        style={{
          background: 'color-mix(in srgb, var(--color-bg-glass) 92%, transparent)',
          ...(open() ? { 'box-shadow': 'inset 0 0 0 1px var(--color-border-accent)' } : {}),
        }}
      >
        <Show when={Icon}>
          {(I) => {
            const Comp = I();
            return <Comp class="h-4 w-4 shrink-0 text-tertiary" />;
          }}
        </Show>
        <span class={cn('flex-1 truncate', props.value ? 'text-primary' : 'text-tertiary')}>
          {props.value || props.placeholder || '—'}
        </span>
        <ChevronDown
          class={cn(
            'h-4 w-4 shrink-0 text-tertiary transition-transform duration-fast',
            open() && 'rotate-180',
          )}
        />
      </button>

      <Popover open={open()} onClose={() => setOpen(false)} anchor={anchor()} align={props.align ?? 'start'}>
        <div
          ref={(el) => (listRef = el)}
          class="glass-menu p-1.5 animate-pop-in overflow-y-auto"
          style={{ width: '108px', 'max-height': '224px' }}
        >
          <For each={slots()}>
            {(slot) => {
              const isSel = () => slot === props.value;
              return (
                <button
                  type="button"
                  data-selected={String(isSel())}
                  class={cn(
                    'press w-full h-8 px-2.5 rounded-md text-[13px] tabular text-left transition-colors',
                    isSel() ? 'text-white font-semibold' : 'text-primary hover:bg-white/8',
                  )}
                  style={isSel() ? { background: 'var(--color-accent)' } : {}}
                  onClick={() => {
                    props.onChange(slot);
                    setOpen(false);
                  }}
                >
                  {slot}
                </button>
              );
            }}
          </For>
        </div>
      </Popover>
    </>
  );
}
